import { useState } from 'react';
import { IconChecklist, IconSparkles } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import '../edu.i18n';
import { Button, Drawer, Segmented, useToast } from '@hana/ui';
import type { Course } from '../data/courses';
import { PASS_SCORE as QUIZ_PASS_PCT } from '../data/logic';
import { LESSONS, QUESTIONS } from '../data/quiz';
import s from '../edu.module.css';

// Ngăn kéo khoá học (board Mint-Training): tab Bài học / Bài kiểm tra; chỉ giả lập trong bộ nhớ, chưa ghi DB.

type Part = 'les' | 'quiz';
const KEYS = 'ABCD';

function Lessons({ c, onOpen }: { c: Course; onOpen: (k: string) => void }) {
  return (
    <>
      {LESSONS.slice(0, c.total).map((k, i) => {
        const done = i < c.done;
        const next = i === c.done;
        return (
          <div key={k} className={s.lesson}>
            <span className={s.lessonDot} data-s={done ? 'done' : next ? 'next' : undefined}>{done ? '✓' : i + 1}</span>
            <div className={s.lessonName}><b>{k}</b><span>{i % 2 ? 'Video' : 'Bài đọc'} {3 + i} phút</span></div>
            <Button variant={next ? 'primary' : 'secondary'} onClick={() => onOpen(k)}>{done ? 'Xem lại' : next ? 'Học' : 'Mở'}</Button>
          </div>
        );
      })}
    </>
  );
}

function Quiz({ qi, sel, checked, ok, onPick }: { qi: number; sel: number | null; checked: boolean; ok: number; onPick: (i: number) => void }) {
  const q = QUESTIONS[qi]!;
  const right = checked && sel === q.answer;
  return (
    <>
      <div className={s.qHead}><b>Câu {qi + 1} / {QUESTIONS.length}</b><span>Điểm đạt {QUIZ_PASS_PCT}% · đang {ok}/{QUESTIONS.length}</span></div>
      <div className={s.qBars}>{QUESTIONS.map((_, i) => <i key={i} data-s={i < qi ? 'done' : i === qi ? 'cur' : undefined} />)}</div>
      <div className={s.qCard}>
        <div className={s.qText}>{q.q}</div>
        <div className={s.qOpts}>
          {q.options.map((o, i) => (
            <button key={o} type="button" aria-pressed={sel === i} data-on={sel === i ? '1' : undefined} data-r={checked && i === q.answer ? 'ok' : checked && sel === i ? 'bad' : undefined} onClick={() => !checked && onPick(i)}>
              <span>{KEYS[i]}</span>{o}
            </button>
          ))}
        </div>
      </div>
      {checked && (
        <div className={s.qFb} data-ok={right ? '1' : undefined}>
          <span><IconSparkles size={15} aria-hidden="true" /></span>
          <div><b>{right ? 'Đúng rồi.' : 'Chưa đúng.'}</b><div>{q.explain}</div><em>Nguồn: {q.source}</em></div>
        </div>
      )}
      <label className={s.qAsk} title="Ask HANA làm gia sư: câu hỏi sinh từ SOP đang hiệu lực, giải thích trích đúng mục tài liệu">
        <IconSparkles size={15} aria-hidden="true" />
        <input aria-label="Hỏi Ask HANA về câu này" placeholder="Hỏi Ask HANA: vì sao không chọn B?" />
      </label>
    </>
  );
}

export function CourseDrawer({ course, part: p0, onClose }: { course: Course | null; part?: Part; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [part, setPart] = useState<Part>('les');
  const [qi, setQi] = useState(0);
  const [sel, setSel] = useState<number | null>(null);
  const [checked, setChecked] = useState(false);
  const [ok, setOk] = useState(0);
  const [seen, setSeen] = useState<Course | null>(null);
  // mở khoá mới: đặt lại như openC của board (khoá học xong chưa đạt → vào thẳng bài kiểm tra)
  if (course && course !== seen) {
    setSeen(course);
    setPart(p0 ?? (course.done === course.total && !course.passed ? 'quiz' : 'les'));
    setQi(0); setSel(null); setChecked(false); setOk(0);
  }
  if (!course) return null;
  const last = qi === QUESTIONS.length - 1;
  const startQuiz = () => { setPart('quiz'); setQi(0); setSel(null); setChecked(false); setOk(0); };
  const act = () => {
    if (!checked) {
      if (sel === null) return;
      setChecked(true);
      if (sel === QUESTIONS[qi]!.answer) setOk(ok + 1);
      return;
    }
    if (!last) { setQi(qi + 1); setSel(null); setChecked(false); return; }
    const pct = Math.round((ok / QUESTIONS.length) * 100);
    onClose();
    toast({ msg: pct >= QUIZ_PASS_PCT ? `Đạt ${pct}% · đã cấp chứng chỉ nội bộ` : `Đạt ${pct}% · chưa đạt ${QUIZ_PASS_PCT}%, làm lại sau 24 giờ` });
  };
  const foot = part === 'les' ? `${course.done}/${course.total} bài · hạn ${course.due}` : `Điểm đạt ${QUIZ_PASS_PCT}% · làm lại sau 24 giờ`;
  return (
    <Drawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={course.k}
      description={course.cat}
      footer={
        <>
          <span className={s.dFoot}>{foot}</span>
          {part === 'quiz'
            ? <Button variant="primary" disabled={!checked && sel === null} onClick={act}>{!checked ? 'Kiểm tra' : last ? 'Nộp bài' : 'Câu tiếp'}</Button>
            : <Button variant="primary" icon={<IconChecklist size={16} aria-hidden="true" />} onClick={startQuiz}>{t('ed.takeQuiz')}</Button>}
        </>
      }
    >
      <Segmented label="Phần khoá học" value={part} onChange={setPart} items={[{ key: 'les', label: 'Bài học' }, { key: 'quiz', label: 'Bài kiểm tra' }]} />
      <div className={s.dBody}>
        {part === 'les'
          ? <Lessons c={course} onOpen={(k) => toast({ msg: `Đã mở bài: ${k}` })} />
          : (
            <Quiz qi={qi} sel={sel} checked={checked} ok={ok} onPick={setSel} />
          )}
      </div>
    </Drawer>
  );
}

/** Dùng trong màn: `open(khoá, phần?)` + `node` đặt cuối JSX. */
export function useCourseDrawer() {
  const [st, setSt] = useState<{ c: Course; part?: Part } | null>(null);
  return {
    open: (c: Course, part?: Part) => setSt({ c, part }),
    node: <CourseDrawer course={st?.c ?? null} part={st?.part} onClose={() => setSt(null)} />,
  };
}
